import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

const ACCENT = '#10B981';
const WA_GREEN = '#25D366';
const TEXT = 'var(--text)';
const MUTED = 'var(--muted)';
const BORDER = 'var(--border)';
const BG2 = 'var(--bg2)';

const DEFAULT_WELCOME = `Namaste! 🙏 Welcome to [Business Name]. How can I help you today?
1️⃣ View Products
2️⃣ Track Order
3️⃣ Talk to Human`;

export function WhatsAppCommercePage() {
  const [waNumber, setWaNumber] = useState('');
  const [displayName, setDisplayName] = useState('ODC Store');
  const [welcome, setWelcome] = useState(DEFAULT_WELCOME);

  useEffect(() => {
    void (async () => {
      try {
        const { data } = await api.get<{ business: { business_name: string } | null }>('/business/me');
        if (data.business?.business_name) {
          setDisplayName(data.business.business_name);
          setWelcome((w) =>
            w.includes('[Business Name]')
              ? w.replace(/\[Business Name\]/g, data.business!.business_name)
              : w
          );
        }
      } catch {
        /* demo */
      }
    })();
  }, []);

  const previewName = displayName || 'ODC Store';
  const welcomePreview = welcome.replace(/\[Business Name\]/g, previewName);

  return (
    <div className="odc-page relative z-10 space-y-8">
      <header
        className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between"
        style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 24 }}
      >
        <div className="min-w-0 flex-1 space-y-3">
          <span
            className="inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em]"
            style={{ background: `${WA_GREEN}28`, color: '#B8F5C8', border: `1px solid ${WA_GREEN}66` }}
          >
            WHATSAPP COMMERCE
          </span>
          <h1 className="text-2xl font-bold md:text-3xl" style={{ color: TEXT }}>
            Sell on WhatsApp — Fully Automated
          </h1>
          <p className="max-w-xl text-sm leading-relaxed" style={{ color: MUTED }}>
            Take orders, collect payments, send delivery updates — all on WhatsApp. Zero human involvement.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          {['500M+ WhatsApp Users in India', '3x Higher Open Rate vs Email', '₹0 Setup Cost'].map((t) => (
            <span
              key={t}
              className="shrink-0 rounded-full border px-3 py-2 text-[11px] font-semibold"
              style={{ borderColor: `${WA_GREEN}55`, background: `${WA_GREEN}18`, color: '#A7F3D0' }}
            >
              {t}
            </span>
          ))}
        </div>
      </header>

      <section>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {(
            [
              { icon: '📱', title: 'Customer Messages', body: "Customer sends 'Hi' on WhatsApp" },
              { icon: '🤖', title: 'AI Responds', body: 'AI shows catalog, takes order, answers queries' },
              { icon: '💳', title: 'Payment Collected', body: 'UPI/card payment link auto-sent' },
              { icon: '🚚', title: 'Auto Updates', body: 'Order confirmed, packed, shipped — auto notified' },
            ] as const
          ).map((s) => (
            <div key={s.title} className="odc-card !p-5 text-center" style={{ transform: 'none' }}>
              <div className="text-3xl">{s.icon}</div>
              <h3 className="mt-2 text-sm font-bold" style={{ color: TEXT }}>
                {s.title}
              </h3>
              <p className="mt-2 text-xs leading-relaxed" style={{ color: MUTED }}>
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
        <div className="odc-card space-y-5 !p-7" style={{ transform: 'none' }}>
          <div>
            <h2 className="text-lg font-bold" style={{ color: TEXT }}>
              Setup WhatsApp Commerce
            </h2>
            <p className="mt-1 text-sm" style={{ color: MUTED }}>
              Connect once — automate forever
            </p>
          </div>
          <div className="flex flex-wrap gap-2 border-b pb-4" style={{ borderColor: BORDER }}>
            {['Connect Number', 'Add Catalog', 'Go Live'].map((label, i) => (
              <span
                key={label}
                className="rounded-full px-3 py-1 text-[11px] font-bold"
                style={{
                  background: i === 0 ? `${ACCENT}24` : BG2,
                  color: i === 0 ? ACCENT : MUTED,
                  border: `1px solid ${i === 0 ? ACCENT : BORDER}`,
                }}
              >
                Step {i + 1}: {label}
              </span>
            ))}
          </div>
          <div className="space-y-4">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
                WhatsApp Business Number
              </span>
              <input
                className="odc-input mt-1.5 w-full"
                placeholder="+91 98765 43210"
                value={waNumber}
                onChange={(e) => setWaNumber(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
                Business Display Name
              </span>
              <input
                className="odc-input mt-1.5 w-full"
                placeholder="Your Business Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
                Welcome Message
              </span>
              <textarea
                className="odc-input mt-1.5 min-h-[5.5rem] w-full resize-y"
                rows={3}
                value={welcome}
                onChange={(e) => setWelcome(e.target.value)}
              />
            </label>
            <button type="button" className="odc-btn-primary w-full justify-center !py-3">
              Connect WhatsApp →
            </button>
            <p className="text-xs leading-relaxed" style={{ color: MUTED }}>
              Requires WhatsApp Business API account. Get free access at{' '}
              <a
                href="https://business.whatsapp.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: ACCENT, fontWeight: 600 }}
              >
                business.whatsapp.com
              </a>
            </p>
          </div>
        </div>

        <div className="odc-card !flex !flex-col !p-7" style={{ transform: 'none' }}>
          <h2 className="text-lg font-bold" style={{ color: TEXT }}>
            WhatsApp Preview
          </h2>
          <p className="mt-1 text-sm" style={{ color: MUTED }}>
            How customers will see your bot
          </p>
          <div
            className="mt-4 flex flex-1 flex-col overflow-hidden rounded-2xl border"
            style={{ borderColor: BORDER, background: '#ECE5DD', minHeight: 420 }}
          >
            <div
              className="flex items-center justify-between px-3 py-2.5"
              style={{ background: WA_GREEN, color: '#fff' }}
            >
              <span className="text-sm font-bold">🤖 {previewName}</span>
              <span className="text-[11px] opacity-90">Online</span>
            </div>
            <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-3" style={{ fontSize: 13 }}>
              <div className="self-start max-w-[88%] rounded-lg px-2.5 py-1.5 shadow-sm" style={{ background: '#fff' }}>
                Hi!
              </div>
              <div
                className="self-end max-w-[88%] rounded-lg px-2.5 py-1.5 shadow-sm"
                style={{ background: '#DCF8C6', color: '#111' }}
              >
                {welcomePreview.split('\n')[0]}
                <br />
                <span className="text-[11px] opacity-80">…</span>
              </div>
              <div className="self-start max-w-[88%] rounded-lg px-2.5 py-1.5 shadow-sm" style={{ background: '#fff' }}>
                1
              </div>
              <div
                className="self-end max-w-[88%] rounded-lg px-2.5 py-1.5 shadow-sm"
                style={{ background: '#DCF8C6', color: '#111' }}
              >
                Here are our products: [catalog preview]
              </div>
              <div className="self-start max-w-[88%] rounded-lg px-2.5 py-1.5 shadow-sm" style={{ background: '#fff' }}>
                I want item 1
              </div>
              <div
                className="self-end max-w-[88%] rounded-lg px-2.5 py-1.5 shadow-sm"
                style={{ background: '#DCF8C6', color: '#111' }}
              >
                Great! Total: ₹999. Pay here: [payment link] 💳
              </div>
            </div>
            <div className="border-t p-2" style={{ borderColor: 'rgba(0,0,0,0.06)', background: '#F0F0F0' }}>
              <input
                disabled
                className="w-full rounded-full border-0 px-3 py-2 text-sm"
                style={{ background: '#fff', color: MUTED }}
                placeholder="Type a message"
              />
            </div>
          </div>
        </div>
      </div>

      <section>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(
            [
              { icon: '🛒', title: 'Auto Catalog', body: 'Product catalog synced from your inventory' },
              { icon: '💳', title: 'UPI Payments', body: 'Razorpay/UPI payment links auto-generated' },
              { icon: '📦', title: 'Order Tracking', body: 'Customers track orders via WhatsApp' },
              { icon: '🔄', title: 'Auto Follow-up', body: 'Abandoned cart? Auto reminder in 30 min' },
              { icon: '📊', title: 'Commerce Analytics', body: 'Revenue, orders, conversion via WhatsApp' },
              { icon: '🌐', title: 'Multi-Language', body: 'Hindi, English, Hinglish — AI detects auto' },
            ] as const
          ).map((f) => (
            <div key={f.title} className="odc-card !p-5" style={{ transform: 'none' }}>
              <span className="text-2xl">{f.icon}</span>
              <h3 className="mt-2 text-sm font-bold" style={{ color: TEXT }}>
                {f.title}
              </h3>
              <p className="mt-2 text-xs leading-relaxed" style={{ color: MUTED }}>
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section
        className="odc-card overflow-hidden !p-8 text-center"
        style={{
          transform: 'none',
          background: `linear-gradient(135deg, ${ACCENT}22, var(--card))`,
          borderColor: `${ACCENT}44`,
        }}
      >
        <h2 className="text-xl font-bold" style={{ color: TEXT }}>
          Ready to sell on WhatsApp?
        </h2>
        <button type="button" className="odc-btn-primary mx-auto mt-5 justify-center !px-8 !py-3">
          Connect WhatsApp Business API
        </button>
        <p className="mt-3 text-xs" style={{ color: MUTED }}>
          Currently in beta — joining waitlist
        </p>
      </section>
    </div>
  );
}
